/* Falling baguettes for the Design Ethos page. Added by Claude.
   Loaded only by templates/page.ethos.json.

   WHAT IT IS. A set channel of baguette-cut diamonds runs edge to edge along the foot
   of the page. It climbs in staggered courses from a flat centre up to tall columns at
   both margins, framing the area where copy and images sit. Above it, single stones
   fall down the margins, some turning ninety degrees on their own centre before coming
   to rest on the stack.

   THE STONE. A baguette is a step cut: an outer rectangle, an inner rectangle for the
   table, and four mitres running corner to corner between them. Square corners, not
   chamfered - that is what separates it from the octagonal geometry used on the
   buttons and header. Drawn as SVG strokes, so it stays crisp at any size and costs
   nothing to load.

   THE MODULE. Three variants, each four units wide and two rows deep, built from a
   long baguette, a half, and quarters standing upright. They tile without a gap, and
   alternate so a tall column never repeats the same two courses.

   THE SILHOUETTE. Height per module follows a curve from the centre outward, plus a
   deterministic nudge of a course either way, so the climb staggers like a Tetris
   board rather than rising as a clean ramp or a sheer wall. Deterministic rather than
   random so the profile is stable rather than reshuffling on every reset.

   MOBILE runs the same logic with fewer, smaller stones: a smaller unit, narrower
   margin bands, a lower frame and a wider flat centre. A phone has no side margin to
   spare, so it reads as a low wall along the foot rather than a tall surround - any
   more and it would crowd the measure.

   THREE THINGS THAT WENT WRONG EARLIER, worth not repeating:

   1. Collision tested against Math.floor(y), so a stone could sink up to a full cell
      into the stack before the test fired, then snap back. Each stone now computes the
      row it will rest on before it starts falling, and stops exactly there.

   2. Rows above the field counted as blocked, so every stone settled the instant it
      spawned, tripped the reset and repacked the whole base - every frame. That read
      as the settled stones buzzing.

   3. The grid was rounded DOWN to a whole number of modules, leaving a strip at the
      right edge the pattern never reached. It now rounds up and lets the last module
      run past the edge, where the container clips it.

   Settled stones are painted once and never touched again; only the falling stone is
   redrawn per frame.

   Hidden for prefers-reduced-motion. aria-hidden and pointer-events: none - it is
   decoration and must never intercept a click or be read aloud. */

(function () {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var MOBILE = window.innerWidth < 900;

  var U = MOBILE ? 17 : 26;              // unit size
  var BANDPX = MOBILE ? 62 : 150;        // margin band the stones fall in
  var MAXFRAC = MOBILE ? 0.32 : 0.60;    // how high the corners reach
  var FLAT = MOBILE ? 0.44 : 0.30;       // share of the width left flat and open
  var FALL = MOBILE ? 0.030 : 0.037;

  var NS = 'http://www.w3.org/2000/svg';
  var MODW = 4, MODH = 2;
  var LINE = 'rgba(255,255,255,0.95)', DIM = 'rgba(255,255,255,0.40)';

  function el(n, a) {
    var e = document.createElementNS(NS, n);
    for (var k in a) e.setAttribute(k, a[k]);
    return e;
  }

  function bag(w, h, dim) {
    var g = el('g', {}), s = dim ? DIM : LINE;
    var i = Math.max(2, Math.round(Math.min(w, h) * 0.24));
    g.appendChild(el('rect', { x: 0.5, y: 0.5, width: w - 1, height: h - 1, fill: 'none', stroke: s, 'stroke-width': 1 }));
    g.appendChild(el('rect', { x: i, y: i, width: w - i * 2, height: h - i * 2, fill: 'none', stroke: s, 'stroke-width': 1 }));
    [[0.5, 0.5, i, i], [w - 0.5, 0.5, w - i, i], [w - 0.5, h - 0.5, w - i, h - i], [0.5, h - 0.5, i, h - i]]
      .forEach(function (d) {
        g.appendChild(el('line', { x1: d[0], y1: d[1], x2: d[2], y2: d[3], stroke: s, 'stroke-width': 1.1 }));
      });
    return g;
  }

  function jitter(i) { var x = Math.sin(i * 12.9898) * 43758.5453; return x - Math.floor(x); }

  var MODA = [{ c: 0, r: 0, w: 4, h: 1 }, { c: 0, r: 1, w: 2, h: 1 }, { c: 2, r: 1, w: 1, h: 1 }, { c: 3, r: 1, w: 1, h: 1 }];
  var MODB = [{ c: 0, r: 0, w: 2, h: 1 }, { c: 2, r: 0, w: 2, h: 1 }, { c: 0, r: 1, w: 4, h: 1 }];
  var MODC = [{ c: 0, r: 0, w: 1, h: 2 }, { c: 1, r: 0, w: 2, h: 1 }, { c: 3, r: 0, w: 1, h: 2 }, { c: 1, r: 1, w: 2, h: 1 }];

  function build(host) {
    var W = host.clientWidth, H = host.clientHeight;
    if (!W || !H) return null;

    var COLS = Math.ceil(W / U / MODW) * MODW;
    var ROWS = Math.floor(H / U);
    if (ROWS < 5) return null;
    var BANDC = Math.max(MODW, Math.round(BANDPX / U));

    var svg = el('svg', {
      width: COLS * U, height: ROWS * U,
      viewBox: '0 0 ' + COLS * U + ' ' + ROWS * U, 'aria-hidden': 'true'
    });
    svg.setAttribute('style', 'position:absolute;left:0;top:' + (H - ROWS * U) + 'px');
    host.appendChild(svg);

    var setL = el('g', {}), actL = el('g', {});
    svg.appendChild(setL); svg.appendChild(actL);

    var grid = [];
    for (var r = 0; r < ROWS; r++) grid.push(new Array(COLS).fill(false));

    function put(c, w, h, row, dim) {
      if (c < 0 || c + w > COLS || row < 0 || row + h > ROWS) return false;
      for (var r = row; r < row + h; r++) for (var x = c; x < c + w; x++) if (grid[r][x]) return false;
      for (var a = row; a < row + h; a++) for (var b = c; b < c + w; b++) grid[a][b] = true;
      var g = bag(w * U, h * U, dim);
      g.setAttribute('transform', 'translate(' + c * U + ',' + row * U + ')');
      setL.appendChild(g);
      return true;
    }

    function free(c, w, h, row) {
      if (c < 0 || c + w > COLS || row + h > ROWS) return false;
      for (var r = Math.max(0, row); r < row + h; r++)
        for (var x = c; x < c + w; x++) if (grid[r][x]) return false;
      return true;
    }

    function layModule(col, row, which) {
      (which === 0 ? MODA : (which === 1 ? MODB : MODC)).forEach(function (s) {
        put(col + s.c, s.w, s.h, row + s.r, true);
      });
    }

    function courses(col) {
      var mid = COLS / 2, d = Math.abs(col + MODW / 2 - mid) / mid;
      var maxC = Math.max(2, Math.floor(ROWS * MAXFRAC / MODH));
      if (d <= FLAT) return 1;
      var t = (d - FLAT) / (1 - FLAT);
      var base = 1 + Math.pow(t, 1.35) * (maxC - 1);
      var j = Math.round((jitter(col) * 2.6) - 1.1);
      return Math.max(1, Math.min(maxC, Math.round(base) + j));
    }

    function pack() {
      for (var col = 0; col < COLS; col += MODW) {
        var n = courses(col);
        for (var k = 0; k < n; k++) layModule(col, ROWS - MODH * (k + 1), Math.floor(jitter(col * 7 + k * 3) * 3));
      }
    }

    function restRow(c, w, h, from) {
      var row = Math.max(-h, Math.ceil(from));
      while (free(c, w, h, row + 1)) row++;
      return row;
    }

    function inBand(c, w) { return (c + w <= BANDC) || (c >= COLS - BANDC); }

    var p = null;

    function make() {
      var L = [1, 2, 2, 4][Math.floor(Math.random() * 4)];
      var v = (L === 1) || Math.random() < 0.28;
      var w = v ? 1 : L, h = v ? (L === 1 ? 2 : L) : 1;
      if (w > BANDC) w = BANDC;
      var left = Math.random() < 0.5;
      var c = left ? Math.floor(Math.random() * (BANDC - w + 1))
                   : COLS - BANDC + Math.floor(Math.random() * (BANDC - w + 1));
      var o = { w: w, h: h, c: c, y: -h, ang: 0, turning: false,
                spin: Math.random() < 0.5 ? (1 + Math.random() * 4) : -1 };
      o.rest = restRow(c, w, h, -h);
      return o;
    }

    function drawA() {
      while (actL.firstChild) actL.removeChild(actL.firstChild);
      if (!p) return;
      var g = bag(p.w * U, p.h * U, false);
      g.setAttribute('transform', 'translate(' + p.c * U + ',' + (p.y * U) + ') rotate(' + p.ang + ',' + (p.w * U / 2) + ',' + (p.h * U / 2) + ')');
      actL.appendChild(g);
    }

    function land() {
      put(p.c, p.w, p.h, p.rest, true);
      while (actL.firstChild) actL.removeChild(actL.firstChild);
      p = make();
      if (p.rest < 2) {
        for (var r = 0; r < ROWS; r++) grid[r].fill(false);
        while (setL.firstChild) setL.removeChild(setL.firstChild);
        pack();
        p = make();
      }
    }

    pack();
    p = make();

    return function step() {
      if (!p) p = make();
      if (p.turning) {
        p.ang += 7;
        if (p.ang >= 90) {
          p.ang = 0; p.turning = false;
          var w = p.h, h = p.w;
          if (p.c + w > COLS) p.c = COLS - w;
          if (!inBand(p.c, w)) p.c = (p.c < COLS / 2) ? Math.max(0, BANDC - w) : COLS - BANDC;
          p.w = w; p.h = h;
          p.rest = restRow(p.c, p.w, p.h, p.y);
        }
        drawA();
      } else if (p.spin > 0 && p.y >= p.spin && p.y < p.rest - 1) {
        p.spin = -1; p.turning = true; drawA();
      } else {
        var n = p.y + FALL;
        if (n >= p.rest) { p.y = p.rest; drawA(); land(); }
        else { p.y = n; drawA(); }
      }
    };
  }

  function init() {
    var main = document.querySelector('#MainContent');
    if (!main) return;

    var host = document.createElement('div');
    host.className = 'mr-gem-field';
    host.setAttribute('aria-hidden', 'true');
    main.appendChild(host);

    var step = build(host);
    if (!step) return;

    var last = 0;
    (function loop(t) {
      if (t - last > 28) { step(); last = t; }
      window.requestAnimationFrame(loop);
    })(0);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
